import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {getApolloClient} from 'graphql-client/ApolloClient';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {isAbortError, postJSONPromise} from 'util/Net';
import {abortableDelay, withLoadingScreen} from 'util/Promises';

import type {MCPServerPingQuery, MCPServerPingQueryVariables} from './__generated/MCPServerPingQuery';
import {sharedStyles} from './McpModalStyles';
import {MCP_SERVER_PING_QUERY} from './useMCPPing';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutes timeout

interface ConnectMCPModalProps {
  config: {
    id: string;
    name: string;
  };
  onRequestClose: () => void;
  onRequestSubmit: (userConfigId: string) => void;
}

export default function ConnectMCPModal(props: ConnectMCPModalProps): JSX.Element {
  const {config, onRequestClose, onRequestSubmit} = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false);
  const [authRedirectUrl, setAuthRedirectUrl] = useState<string | null>(null);
  /** Aborts in-flight delay between poll iterations when stopping or unmounting. */
  const pollAbortRef = useRef<AbortController | null>(null);
  /** False after stop/unmount so in-flight work does not update state. */
  const pollingActiveRef = useRef(false);

  const queryPingStatus = useCallback(async (): Promise<MCPServerPingQuery['mcpServerPing']> => {
    const result = await getApolloClient().query<MCPServerPingQuery, MCPServerPingQueryVariables>({
      query: MCP_SERVER_PING_QUERY,
      variables: {serverId: config.id},
      fetchPolicy: 'network-only',
    });
    return result.data.mcpServerPing;
  }, [config.id]);

  const stopPolling = useCallback(() => {
    pollingActiveRef.current = false;
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
  }, []);

  // Cleanup polling on unmount
  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  const startPollingForAuth = useCallback(
    (userConfigId: string) => {
      stopPolling();
      const abortController = new AbortController();
      pollAbortRef.current = abortController;
      pollingActiveRef.current = true;
      const startTime = Date.now();

      const runOAuthPollLoop = async (): Promise<void> => {
        while (pollingActiveRef.current) {
          await abortableDelay(POLL_INTERVAL_MS, abortController.signal);
          if (!pollingActiveRef.current) {
            return;
          }

          if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
            stopPolling();
            setIsWaitingForAuth(false);
            notifications.error('Authentication timed out. Please try again.');
            onRequestClose();
            return;
          }

          let pingResult: MCPServerPingQuery['mcpServerPing'];
          try {
            pingResult = await queryPingStatus();
          } catch (error) {
            if (!pollingActiveRef.current) {
              return;
            }
            // Keep polling for Forbidden — user may still be authenticating.
            if (error instanceof Error && error.message.includes('Forbidden')) {
              continue;
            }
            stopPolling();
            setIsWaitingForAuth(false);
            throw error;
          }
          if (!pollingActiveRef.current) {
            return;
          }

          if (!pingResult.needsRedirect) {
            stopPolling();
            setIsWaitingForAuth(false);
            notifications.success(`${config.name} connected successfully`);
            onRequestSubmit(userConfigId);
            return;
          }
        }
      };

      runOAuthPollLoop().catch((error: unknown) => {
        if (!isAbortError(error)) {
          notifications.error(`Failed to connect ${config.name}. Please try again.`);
          onRequestClose();
        }
      });
    },
    [config.name, onRequestClose, onRequestSubmit, queryPingStatus, stopPolling]
  );

  async function handleSubmit(): Promise<void> {
    try {
      setIsSubmitting(true);
      const response = await withLoadingScreen(
        postJSONPromise({
          url: '/1/api/llm/mcp-server-user-configs',
          data: {mcpServerConfigId: config.id},
        }),
        'Connecting to MCP Server...'
      );

      const userConfigId = response.userConfigId as string;
      const pingResult = await queryPingStatus();

      if (pingResult.needsRedirect && pingResult.redirectUrl) {
        window.open(pingResult.redirectUrl, '_blank');
        setAuthRedirectUrl(pingResult.redirectUrl);
        setIsWaitingForAuth(true);
        startPollingForAuth(userConfigId);
        return;
      }

      notifications.success(`${config.name} connected successfully`);
      onRequestSubmit(userConfigId);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isWaitingForAuth) {
    return (
      <Modal
        title="Waiting for authentication"
        onRequestClose={onRequestClose}
        buttons={[{text: 'Cancel', cancel: true}]}
      >
        <p className={css(styles.description)}>Complete authentication in the new tab, then return here.</p>
        <div className={css(styles.infoRow)}>
          <div className={css(styles.iconWrapper)}>
            <BenchlingIcon icon="refresh" className={css(sharedStyles.spinningIcon)} />
          </div>
          <div className={css(styles.infoContent)}>
            <p className={css(styles.infoTitle)}>Waiting for {config.name}...</p>
            <p className={css(styles.infoSubtext)}>This will update automatically once you authenticate</p>
          </div>
        </div>
        {authRedirectUrl && (
          <p className={css(styles.fallbackLink)}>
            Didn't get redirected?{' '}
            <a href={authRedirectUrl} target="_blank" rel="noopener noreferrer">
              Click here to open the authorization URL manually
            </a>
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title="Connect your account"
      onRequestSubmit={handleSubmit}
      onRequestClose={onRequestClose}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Continue', submit: true, style: ButtonStyle.PRIMARY, disabled: isSubmitting},
      ]}
    >
      <p className={css(styles.description)}>
        Allow Benchling AI tools to access additional context on your behalf.
      </p>
      <div className={css(styles.infoRow)}>
        <div className={css(styles.iconWrapper)}>
          <BenchlingIcon icon="sequence" />
        </div>
        <div className={css(styles.infoContent)}>
          <p className={css(styles.infoTitle)}>{config.name} will open a new tab</p>
          <p className={css(styles.infoSubtext)}>Authenticate to connect your account</p>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  description: {
    marginBottom: 16,
    color: 'var(--text-primary)',
    fontSize: 13,
    lineHeight: '16px',
  },
  infoRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  },
  iconWrapper: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  infoTitle: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
    color: 'var(--text-primary)',
  },
  infoSubtext: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '16px',
    color: 'var(--text-primary)',
  },
  fallbackLink: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: '16px',
    color: 'var(--text-secondary)',
  },
});
